import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import PageContainer from '../layout/PageContainer';
import { Alert } from '@mui/material';
import type { Customer } from '../../models/Customer';
import { GET_CUSTOMER_BY_ID_QUERY } from '../../queries/CustomerQueries';
import { PackagingType, ProductInventoryItem } from '../../models/ProductInventoryItem';
import {
  DataGrid,
  GridColDef,
  GridEventListener,
  gridClasses,
} from '@mui/x-data-grid';

const INITIAL_PAGE_SIZE = 100;

export default function CustomerInventory() {
  const { customerId } = useParams();
  const navigate = useNavigate();

  const [getCustomerById] = useLazyQuery(
    GET_CUSTOMER_BY_ID_QUERY);

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(customerId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getCustomerByIdError } = await getCustomerById(
          { variables: { id } }
        );

        setIsLoading(false);
        setError(null);

        if (getCustomerByIdError) {
          setError(getCustomerByIdError);
        }

        if (data?.customer) {
          setCustomer(data?.customer);
        } else {
          setError({ message: 'No customer found' } as Error);
        }

      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [customerId, getCustomerById]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRowClick = useCallback<GridEventListener<'rowClick'>>(
    ({ row }) => {
      navigate(`/dashboard/products/${row.id}/product-inventory-items`);
    },
    [navigate],
  );

  const handleBack = useCallback(() => {
    navigate(`/dashboard/customers/${customerId}`);
  }, [customerId, navigate]);

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Product Name',
        flex: 1,
        display: 'flex'
      },
      {
        field: 'jarsQuantityOnHand',
        headerName: 'Jars on Hand',
        type: 'number',
        flex: 1,
        display: 'flex',
        valueGetter: (_, row) =>
          (row.productInventoryItems || []).filter(
            (item: ProductInventoryItem) => item.packagingType === PackagingType.JAR).map((item: ProductInventoryItem) => (item.quantityOnHand || 0)).reduce((a: number, b: number) => a + b, 0) || '0'
      },
      {
        field: 'pourchesQuantityOnHand',
        headerName: 'Pouches on Hand',
        type: 'number',
        flex: 1,
        display: 'flex',
        valueGetter: (_, row) =>
          (row.productInventoryItems || []).filter(
            (item: ProductInventoryItem) => item.packagingType === PackagingType.POUCH).map((item: ProductInventoryItem) => (item.quantityOnHand || 0)).reduce((a: number, b: number) => a + b, 0) || '0'
      },
      {
        field: 'bulkQuantityOnHand',
        headerName: 'Bulk (lbs)',
        type: 'number',
        flex: 1,
        display: 'flex',
        valueGetter: (_, row) =>
          (row.productInventoryItems || []).filter(
            (item: ProductInventoryItem) => item.packagingType === PackagingType.BULK).map((item: ProductInventoryItem) => (item.quantityOnHand || 0)).reduce((a: number, b: number) => a + b, 0).toFixed(3) || '0.000'
      }
    ],
    [],
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          m: 1,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Alert severity="error">{error.message}</Alert>
      </Box>
    );
  }

  const pageTitle = `${customer?.name} Inventory`;

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[
        { title: 'Customers', path: '/dashboard/customers' },
        { title: `${customer?.name}`, path: `/dashboard/customers/${customer?.id}` },
        { title: 'Inventory' }
      ]}
    >
      <Box
        sx={{ width: '100%' }}
      >
        <Stack spacing={2} justifyContent="space-between" direction="row" sx={{ width: '100%' }}>
          <Box sx={{ width: '100%' }}>
            <DataGrid
              density="compact"
              rows={customer?.products}
              columns={columns}
              loading={isLoading}
              pageSizeOptions={[INITIAL_PAGE_SIZE]}
              onRowClick={handleRowClick}
              getRowHeight={() => 'auto'}
              disableRowSelectionOnClick
              sx={[
                () => ({
                  '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
                  '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
                  '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '22px' },
                  [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                    outline: 'transparent',
                  },
                  [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                  {
                    outline: 'none',
                  },
                  [`& .${gridClasses.row}:hover`]: {
                    cursor: 'pointer',
                  }
                }),
              ]}
              slotProps={{
                loadingOverlay: {
                  variant: 'circular-progress',
                  noRowsVariant: 'circular-progress',
                },
                baseIconButton: {
                  size: 'small',
                },
              }}
            />
          </Box>
        </Stack>
        <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
        </Stack>
      </Box >
    </PageContainer >
  );
}
